'use server';

import { cookies } from "next/headers";
import { redirect } from 'next/navigation'
import { loginSchema } from "./schema";
import { initiateBackendLogin, validateBackendOtp } from "./service";
import { logAdminAuditEvent } from '@/services/admin-audit/service'
import {
  ACCESS_TOKEN_COOKIE,
  REFRESH_TOKEN_COOKIE,
  LEGACY_ADMIN_SESSION_COOKIE,
  LEGACY_ADMIN_USER_COOKIE,
} from '@/lib/auth'

export interface LoginState {
  errors?: {
    email?: string[];
    password?: string[];
    _form?: string[];
  };
  // mfaRequired Path: Action signalisiert dem Client, dass OTP-Step folgt.
  // Client-Component navigiert zu /verify-otp?otpId=...&factor=...
  mfaRequired?: boolean;
  otpId?: string;
  factor?: 'EMAIL_OTP' | 'TOTP';
  success?: boolean;
}

const LOGIN_ERROR_MESSAGES: Record<string, string> = {
  NOT_ADMIN: 'Dieser Account hat keine Admin-Berechtigung.',
  ACCOUNT_NOT_ACTIVE: 'Account ist nicht aktiv. Bitte Admin kontaktieren.',
  'Invalid credentials': 'E-Mail oder Passwort ist falsch.',
  PASSWORD_CHANGE_OVERDUE: 'Dein Passwort ist abgelaufen. Bitte Passwort-Reset durchführen.',
  BACKEND_UNREACHABLE: 'Backend nicht erreichbar. Bitte später erneut versuchen.',
}

function mapLoginError(code: string): string {
  return LOGIN_ERROR_MESSAGES[code] ?? code
}

function getAccessTokenCookieOptions(isProduction: boolean, maxAgeSeconds: number) {
  return {
    httpOnly: true,
    secure: isProduction,
    sameSite: 'strict' as const,
    maxAge: maxAgeSeconds,
    path: '/',
  }
}

function getRefreshTokenCookieOptions(isProduction: boolean) {
  // Refresh-Token entspricht Backend-Default (7 Tage)
  return {
    httpOnly: true,
    secure: isProduction,
    sameSite: 'strict' as const,
    maxAge: 60 * 60 * 24 * 7,
    path: '/',
  }
}

async function setAuthCookies(accessToken: string, refreshToken: string, expiresIn: number) {
  const cookieStore = await cookies()
  const isProduction = process.env.NODE_ENV === 'production'
  cookieStore.set(ACCESS_TOKEN_COOKIE, accessToken, getAccessTokenCookieOptions(isProduction, expiresIn))
  cookieStore.set(REFRESH_TOKEN_COOKIE, refreshToken, getRefreshTokenCookieOptions(isProduction))
  // Alte HMAC-Cookies aktiv invalidieren (per Marcos Entscheidung — bestehende Admin-Sessions
  // werden beim Deploy abgemeldet, alle Admins müssen frisch via OTP einloggen)
  cookieStore.set(LEGACY_ADMIN_SESSION_COOKIE, '', { maxAge: 0, path: '/' })
  cookieStore.set(LEGACY_ADMIN_USER_COOKIE, '', { maxAge: 0, path: '/' })
}

export async function login(_prevState: LoginState, formData: FormData): Promise<LoginState> {
  const validatedFields = loginSchema.safeParse({
    email: formData.get("email"),
    password: formData.get("password"),
  });

  if (!validatedFields.success) {
    return { errors: validatedFields.error.flatten().fieldErrors };
  }

  const { email, password } = validatedFields.data;
  const result = await initiateBackendLogin({ email, password });

  if (!result.success) {
    return { errors: { _form: [mapLoginError(result.errorCode)] } };
  }

  if (result.mfaRequired) {
    // mfaRequired-Pfad: Tokens erst nach OTP-Validation, kein Cookie hier setzen.
    // Client-Component navigiert zu /verify-otp.
    return {
      mfaRequired: true,
      otpId: result.otpId,
      factor: result.factor ?? 'EMAIL_OTP',
    }
  }

  // Trusted-Device-Pfad (selten, bleibt vorerst ungenutzt für Admin — Backend
  // könnte hier direkt Tokens liefern wenn Trusted-Device-Cookie mitgesendet würde).
  await setAuthCookies(
    result.tokens.accessToken,
    result.tokens.refreshToken,
    result.tokens.expiresIn,
  )

  try {
    await logAdminAuditEvent({
      action: 'CREATE',
      entity: 'ADMIN_SESSION',
      category: 'AUTH',
      entityId: String(result.user.id),
      oldData: null,
      newData: {
        user_id: result.user.id,
        email: result.user.email,
        flow: 'TRUSTED_DEVICE',
      },
    })
  } catch (error) {
    console.error('admin audit log (trusted-device login) failed:', error)
  }

  redirect('/')
}

export interface VerifyOtpState {
  errors?: { otp?: string[]; _form?: string[] }
  success?: boolean
}

export async function verifyOtp(_prevState: VerifyOtpState, formData: FormData): Promise<VerifyOtpState> {
  const otpId = String(formData.get('otpId') ?? '')
  const otpRaw = String(formData.get('otp') ?? '').trim()
  const factorRaw = String(formData.get('factor') ?? 'EMAIL_OTP')
  const factor: 'EMAIL_OTP' | 'TOTP' | 'RECOVERY' =
    factorRaw === 'TOTP' ? 'TOTP' : factorRaw === 'RECOVERY' ? 'RECOVERY' : 'EMAIL_OTP'

  if (!otpId) {
    return { errors: { _form: ['Session ist abgelaufen. Bitte erneut einloggen.'] } }
  }

  if (factor === 'RECOVERY') {
    // Recovery-Code (XXXX-XXXX-XXXX) — Backend normalisiert Bindestriche/Whitespace.
    if (otpRaw.replace(/[\s-]/g, '').length < 12) {
      return { errors: { otp: ['Bitte einen gültigen Recovery-Code eingeben.'] } }
    }
  } else if (!/^\d{6}$/.test(otpRaw)) {
    return { errors: { otp: ['Bitte den 6-stelligen Code eingeben.'] } }
  }

  const result = await validateBackendOtp(otpId, otpRaw, factor)
  if (!result.success) {
    return { errors: { _form: [mapLoginError(result.errorCode)] } }
  }

  await setAuthCookies(
    result.tokens.accessToken,
    result.tokens.refreshToken,
    result.tokens.expiresIn,
  )

  try {
    await logAdminAuditEvent({
      action: 'CREATE',
      entity: 'ADMIN_SESSION',
      category: 'AUTH',
      entityId: String(result.user.id),
      oldData: null,
      newData: {
        user_id: result.user.id,
        email: result.user.email,
        flow: 'OTP',
      },
    })
  } catch (error) {
    console.error('admin audit log (otp login) failed:', error)
  }

  redirect('/')
}
